<template>
  <div class="user-details-section">
    <div class="results" v-if="user">
      <div class="result-title">User Details</div>
      <div class="table-content">
        <form
          id="details-form"
          class="grid-1"
          autocomplete="off"
          @submit.prevent="updateUserDetails"
        >
          <div class="grid">
            <div class="email inputs">
              <label for="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email Address"
                pattern="^[a-zA-Z0-9_.]+@[a-zA-Z0-9_.]+\.[a-zA-Z0-9_.]+$"
                minlength="7"
                maxlength="320"
                v-model.trim="user.emailAddress"
                required
              />
            </div>
            <div class="inputs">
              <label for="first_name">First Name</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                placeholder="First Name"
                pattern="^[A-Za-z-'-]+$"
                minlength="1"
                maxlength="30"
                v-model.trim="user.firstName"
                required
              />
            </div>
            <div class="inputs">
              <label for="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                placeholder="Last Name"
                pattern="^[A-Za-z-'-]+$"
                minlength="1"
                maxlength="30"
                v-model.trim="user.lastName"
                required
              />
            </div>

            <div class="inputs">
              <label for="year_of_birth">Year Of Birth</label>
              <input
                type="text"
                id="year_of_birth"
                name="year_of_birth"
                placeholder="Year of Birth"
                v-model.trim="user.yearOfBirth"
                readonly
              />
            </div>
          </div>

          <div class="grid-2">
            <div class="inputs">
              <label for="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Phone Number"
                pattern="^\+?[0-9\s\-\(\)]{7,21}$"
                inputmode="numeric"
                minlength="7"
                maxlength="21"
                v-model.trim="user.phoneNumber"
                required
              />
            </div>
            <div class="inputs">
              <label for="address1">Address1</label>
              <input
                type="text"
                id="address1"
                name="address1"
                placeholder="Address1"
                maxlength="100"
                v-model.trim="user.address1"
              />
            </div>
            <div class="inputs">
              <label for="address2">Address2</label>
              <input
                type="text"
                id="address2"
                name="address2"
                placeholder="Address2"
                maxlength="100"
                v-model.trim="user.address2"
              />
            </div>
            <div class="inputs">
              <label for="address3">Address3</label>
              <input
                type="text"
                id="address3"
                name="address3"
                placeholder="Address3"
                maxlength="100"
                v-model.trim="user.address3"
              />
            </div>
            <div class="inputs">
              <label for="postcode">Postcode</label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                placeholder="postcode"
                maxlength="10"
                v-model.trim="user.postcode"
              />
            </div>
            <div class="inputs">
              <label for="town_or_city">TownOrCity</label>
              <input
                type="text"
                id="town_or_city"
                name="town_or_city"
                placeholder="Town Or City"
                maxlength="50"
                v-model.trim="user.townOrCity"
              />
            </div>
          </div>
        </form>
        <div class="button-group">
          <button
            :disabled="isSubmittingUpdateDetails"
            class="btn"
            type="submit"
            form="details-form"
          >
            <span v-if="!isSubmittingUpdateDetails">Update Details</span>
            <LoaderDots v-if="isSubmittingUpdateDetails" />
          </button>
          <button
            :disabled="isSubmittingPasswordReset"
            class="btn"
            @click="sendResetPasswordEmail"
          >
            <span v-if="!isSubmittingPasswordReset">Reset Password</span>
            <LoaderDots v-if="isSubmittingPasswordReset" />
          </button>
          <button
            :disabled="isGettingSetPasswordLink"
            class="btn"
            @click="getSetPasswordLink"
          >
            <span v-if="!isGettingSetPasswordLink">Get Set Password Link</span>
            <LoaderDots v-if="isGettingSetPasswordLink" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <Notes></Notes>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, ref } from "vue";
import { useRoute } from "vue-router";
import UserDetailsResponse from "../responses/UserDetailsResponse";
import UserService from "../services/user-service";
import Notes from "../components/Notes.vue";
import { showAlert } from "../utils/global";
import LoaderDots from "./LoadingButtonDots.vue";

export default defineComponent({
  components: {
    Notes,
    LoaderDots,
  },
  setup() {
    const route = useRoute();

    const userId = route.params.userId;
    const user = ref<UserDetailsResponse | null>(null);
    const isSubmittingPasswordReset = ref(false); // New state for button disabling
    const isSubmittingUpdateDetails = ref(false); // New state for button disabling
    const isGettingSetPasswordLink = ref(false);

    const updateUserDetails = async () => {
      isSubmittingUpdateDetails.value = true;
      await UserService.updateUserDetails(user.value)
        .then((response) => {
          if (response?.message == "Update Successful") {
            showAlert(
              "Details Updated",
              "Your details have been updated.",
              "success",
            );
            isSubmittingUpdateDetails.value = false;
          } else {
            showAlert("Update Failed", response, "error");
            isSubmittingUpdateDetails.value = false;
          }
        })
        .catch((error) => {
          showAlert(
            "Update Failed",
            "There was an error updating your details. Please try again.",
            "error",
          );
        });
      isSubmittingUpdateDetails.value = false;
    };

    const sendResetPasswordEmail = async () => {
      try {
        isSubmittingPasswordReset.value = true;
        const response = await UserService.sendResetPasswordEmail(Number(userId));

        if (response.success) {
          const resetPasswordLink = response.resetPasswordLink;
          
          showAlert(
            "Success!",
            `<ul style="text-align: left; list-style-position: inside;">
                <li>Password reset email sent successfully</li>
                <li>The link to reset the user's password is <span style="font-weight: bold;">${resetPasswordLink}</span></li>
              </ul>`,
            "success",
            true,
          );
        } else {
          showAlert(
            "Failed!",
            "Failed to send password reset email. Please try again.",
            "error",
          );
        }
        isSubmittingPasswordReset.value = false;
      } catch (error) {
        showAlert(
          "Failed",
          "Failed to send password reset email. Please try again.",
          "error",
        );
        isSubmittingPasswordReset.value = false;
      }
    };

    const getSetPasswordLink = async () => {
      try {
        isGettingSetPasswordLink.value = true;
        const response = await UserService.getSetPasswordLink(Number(userId));

        if (response.success) {
          const link = response.setPasswordLink;
          showAlert(
            "Set Password Link",
            `<div style="text-align: left;">
                <p>Copy the link below to send to the user:</p>
                <p style="word-break: break-all; font-weight: bold;">${link}</p>
              </div>`,
            "success",
            true,
          );
        } else {
          showAlert(
            "Failed!",
            response.error || "Failed to retrieve set password link. Please try again.",
            "error",
          );
        }
        isGettingSetPasswordLink.value = false;
      } catch (error) {
        showAlert(
          "Failed",
          "Failed to retrieve set password link. Please try again.",
          "error",
        );
        isGettingSetPasswordLink.value = false;
      }
    };

    onBeforeMount(async () => {
      user.value = await UserService.getUserByUserId(Number(userId));
    });

    return {
      userId,
      user,
      updateUserDetails,
      sendResetPasswordEmail,
      getSetPasswordLink,
      isSubmittingUpdateDetails,
      isSubmittingPasswordReset,
      isGettingSetPasswordLink,
    };
  },
});
</script>

<style scoped>
.user-details-section {
}

input {
  display: flex;
  padding: 1rem;
  width: 100%;
  height: 60px;
}

.inputs {
  position: relative;
}

.table-content {
  padding-inline: 10px;
  padding-top: -30px;
}

.table-content {
  overflow-x: auto;
  position: relative;
  background-color: #fff;
  padding: 80px 40px 20px 40px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  margin-top: -50px;
}
.grid-1 {
  display: grid;
  gap: 2rem;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1rem;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
}

label {
  position: absolute;
  left: 10px;
  top: -10px;

  background-color: white;
  padding: 0rem 0.3rem;
}

@media (max-width: 999px) {
  .grid {
    grid-template-columns: 1fr 1fr;
  }

  .grid-2 {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 700px) {
  .grid {
    grid-template-columns: 1fr;
  }
  .grid-2 {
    grid-template-columns: 1fr;
  }
}

.result-title {
  position: relative;
  min-height: 89px;
  align-content: center;
  background-color: var(--clr-primary);
  padding: 1rem 1rem;
  margin-inline: 2rem;
  color: white;
  font-size: 24px;
  border-radius: 3px;
  box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42),
    0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  background-color: var(--clr-primary);
  padding: 1rem;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border-radius: 3px;
  border: none;
  height: 60px;
  flex: 1 1 0;
  min-width: 180px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
